import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNav } from '../components/BottomNav';
import { AppHeader } from '../components/AppHeader';
import { apiFetch, COMPANY_ID, getAccessToken, isPrivilegedViewer, getAppVariant, switchAppVariant } from '../services/api';
import type { AuthUser } from '../hooks/useAuth';

// システムロールの表示名
const ROLE_LABELS: Record<string, string> = {
  system_admin: 'システム管理者',
  shore_admin: '陸上管理者',
  captain: '船長',
  crew: '船員',
  admin: '管理者',
};
import { getQueue, getPendingCount, getLastSyncTime } from '../utils/syncQueue';
import { triggerSync, onSyncComplete } from '../utils/autoSync';
import { hasLocalFace, removeLocalFace } from '../hooks/useFaceAuth';
import { invalidateFaceBadgeCache } from '../components/FaceBadge';
import { hasFeature } from '../utils/featureFlags';
import { getAlcoholThresholds, fmtMgL } from '../utils/alcoholThresholds';
import { FONT_SCALE_OPTIONS, getFontScale, setFontScale, type FontScale } from '../utils/fontScale';
import { HEADER_OFFSET_OPTIONS, getHeaderOffset, setHeaderOffset } from '../utils/safeArea';
import { isSimpleMode, setSimpleMode } from '../utils/simpleMode';
import { batteryPayload } from '../utils/battery';
import { companionPrinterCheck, companionPrint, type PrinterDiagnosis } from '../services/alcohol-companion';
import { MyAvatarEditor } from '../components/MyAvatarEditor';

interface Props { user: AuthUser; onLogout: () => void; }

// デバイストークン設定・センサー情報・通信（未送信/位置情報送信/同期）は
// 管理者・役職者（船長）向け機能。一般船員(crew)には表示しない。
const PRIVILEGED_ROLES = ['system_admin', 'shore_admin', 'captain', 'super_admin'];
function isPrivilegedRole(role?: string): boolean {
  return PRIVILEGED_ROLES.includes(role ?? '');
}

// Web Crypto API で HMAC-SHA256 を計算する
async function computeHmac(secret: string, message: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', key, enc.encode(message));
  return Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('');
}

function clearDeviceLocally() {
  localStorage.removeItem('catms_device_token');
  localStorage.removeItem('catms_device_vessel_id');
  localStorage.removeItem('catms_device_vessel_name');
}

function sendHeartbeat(token: string, onDeviceDeactivated?: () => void) {
  const doSend = async (lat?: number, lon?: number) => {
    const timestamp = new Date().toISOString();
    const payload = `${timestamp}:${lat ?? ''}:${lon ?? ''}`;
    const signature = await computeHmac(token, payload);
    const bat = await batteryPayload();
    apiFetch('/devices/heartbeat', {
      method: 'POST',
      body: JSON.stringify({ device_token: token, company_id: COMPANY_ID, latitude: lat, longitude: lon, timestamp, signature, ...bat }),
    }).catch((e: Error) => {
      if (e.message.includes('デバイスが見つかりません')) {
        clearDeviceLocally();
        onDeviceDeactivated?.();
      }
    });
  };
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      pos => doSend(pos.coords.latitude, pos.coords.longitude),
      () => doSend(),
      { timeout: 8000, maximumAge: 30000 }
    );
  } else {
    doSend();
  }
}


const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, padding: '16px 4px 8px' }}>{children}</div>
);
const Divider = () => <div style={{ height: 1, background: '#f1f5f9', margin: '0 16px' }} />;

// デバイストークン設定モーダル
function DeviceSetupModal({ onClose, onSaved }: { onClose: () => void; onSaved: (vesselId: string, vesselName: string, token: string) => void }) {
  const [token,   setToken]   = useState('');
  const [error,   setError]   = useState('');
  const [saving,  setSaving]  = useState(false);

  const handleSave = async () => {
    const t = token.trim();
    if (!t) { setError('トークンを入力してください'); return; }
    setSaving(true); setError('');
    try {
      const res = await apiFetch<{ success: boolean; data: { vessel_id: string; vessel_name: string } }>(
        '/devices/verify',
        { method: 'POST', body: JSON.stringify({ device_token: t, company_id: COMPANY_ID }) }
      );
      localStorage.setItem('catms_device_token',      t);
      localStorage.setItem('catms_device_vessel_id',   res.data.vessel_id);
      localStorage.setItem('catms_device_vessel_name', res.data.vessel_name);
      // 船舶キャッシュも更新
      localStorage.setItem('catms_last_vessel_id',   res.data.vessel_id);
      localStorage.setItem('catms_vessel_name',       res.data.vessel_name);
      // 登録直後にハートビート（GPS付き）を送信
      sendHeartbeat(t);
      onSaved(res.data.vessel_id, res.data.vessel_name, t);
    } catch (e: any) {
      setError(e.message ?? 'トークンが無効です');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: '28px 20px 48px', width: '100%', maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>📱 デバイストークンを設定</div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, color: '#94a3b8', cursor: 'pointer' }}>✕</button>
        </div>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: '#64748b' }}>
          管理者から受け取ったデバイストークンを入力してください。この端末が特定の船舶に紐付けられます。
        </p>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#475569', marginBottom: 16 }}>
          <span style={{ fontWeight: 700 }}>デバイストークン</span>
          <input
            value={token}
            onChange={e => setToken(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', fontSize: 14, fontFamily: 'monospace', letterSpacing: 1, outline: 'none' }}
            autoCapitalize="off" autoCorrect="off" spellCheck={false}
          />
        </label>
        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px', fontWeight: 600 }}>⚠️ {error}</p>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '13px', borderRadius: 12, border: '1px solid #e2e8f0', background: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#475569' }}>キャンセル</button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '13px', borderRadius: 12, border: 'none', background: saving ? '#93c5fd' : '#0ea5e9', color: 'white', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '確認中...' : '設定する'}
          </button>
        </div>
      </div>
    </div>
  );
}

export const SettingsPage: React.FC<Props> = ({ user, onLogout }) => {
  const nav = useNavigate();
  // アルコールの判定基準は会社ごとに設定できる。画面に数値を直接書かず、設定値を表示する。
  const alcoTh = getAlcoholThresholds();
  // 役職(position)が付いた一般船員も「役職者」として扱うため、本人の乗船中役職を取得
  const [hasPosition, setHasPosition] = useState(false);
  const [myPosition, setMyPosition]   = useState<string | null>(null);
  const [freshRole, setFreshRole]     = useState<string | null>(null);
  const effectiveRole = freshRole ?? user.user_role;
  const privileged = isPrivilegedRole(effectiveRole) || hasPosition;
  // 表示用の役職: 配乗役職 > システムロール名（最新ロール優先）
  const roleDisplay = myPosition || ROLE_LABELS[effectiveRole] || '船員';
  const [fontScale, setFontScaleState] = useState<FontScale>(() => getFontScale());
  const [headerOffset, setHeaderOffsetState] = useState<number>(() => getHeaderOffset());
  const [simpleOn, setSimpleOn] = useState<boolean>(() => isSimpleMode());
  const [showLogoutConfirm,   setShowLogoutConfirm]   = useState(false);
  const [showDeviceSetup,     setShowDeviceSetup]     = useState(false);
  const [deviceVesselName,    setDeviceVesselName]    = useState<string | null>(null);
  const [deviceToken,         setDeviceToken]         = useState<string | null>(null);
  const [pendingCount,        setPendingCount]        = useState(0);
  const [lastSync,            setLastSync]            = useState<string | null>(null);
  const [syncing,             setSyncing]             = useState(false);
  const [syncResult,          setSyncResult]          = useState<string | null>(null);
  const [gpsSending,          setGpsSending]          = useState(false);
  const [gpsResult,           setGpsResult]           = useState<string | null>(null);

  // ── 顔認証データの登録状態（サーバ優先・オフライン時は端末キャッシュで判定） ──
  const [faceRegistered,      setFaceRegistered]      = useState<boolean | null>(null); // null=確認中
  const [faceUpdatedAt,       setFaceUpdatedAt]       = useState<string | null>(null);
  const [faceLocalOnly,       setFaceLocalOnly]       = useState(false);  // 端末のみ（オフライン判定/未同期）
  const [showFaceDeleteConfirm, setShowFaceDeleteConfirm] = useState(false);
  const [faceDeleting,        setFaceDeleting]        = useState(false);

  useEffect(() => {
    let alive = true;
    const local = hasLocalFace(user.user_id);
    apiFetch<{ data: { registered: boolean; updated_at: string | null } }>('/auth/face-status')
      .then(res => {
        if (!alive) return;
        setFaceRegistered(res.data.registered || local);
        setFaceUpdatedAt(res.data.updated_at);
        setFaceLocalOnly(!res.data.registered && local);
      })
      .catch(() => {
        // オフライン時は端末キャッシュで判定
        if (!alive) return;
        setFaceRegistered(local);
        setFaceLocalOnly(local);
      });
    return () => { alive = false; };
  }, [user.user_id]);

  // ── 船内プリンター設定支援（IP保存・簡易接続チェック・テストページ印刷） ──
  const [printerIp, setPrinterIp] = useState(() => localStorage.getItem('catms_printer_ip') ?? '');
  const [printerChecking, setPrinterChecking] = useState(false);
  const [printerResult, setPrinterResult] = useState<string | null>(null);
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

  const savePrinterIp = (v: string) => {
    setPrinterIp(v);
    localStorage.setItem('catms_printer_ip', v.trim());
  };

  const checkPrinter = async () => {
    const ip = printerIp.trim();
    if (!ip) { setPrinterResult('プリンターのIPアドレスを入力してください'); return; }
    setPrinterChecking(true); setPrinterResult(null);
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 4000);
      // no-cors: 応答が返れば到達可能（内容は読まない）。https→http のため環境により判定不可の場合あり
      await fetch(`http://${ip}/`, { mode: 'no-cors', signal: ctrl.signal });
      clearTimeout(timer);
      setPrinterResult('✅ プリンターに到達できました（同じネットワークにいます）');
    } catch {
      setPrinterResult('⚠️ 到達を確認できませんでした。同じWi-Fiか・IPが正しいかを確認してください（端末の仕様上ここで判定できない場合もあります。最終確認はテストページ印刷で行ってください）');
    }
    setPrinterChecking(false);
  };

  // 診断: コンパニオン（船内共有端末）があれば本格診断、無ければ従来の簡易チェックに落とす
  const [diag, setDiag] = useState<PrinterDiagnosis | null>(null);
  const [diagRunning, setDiagRunning] = useState(false);
  const runDiagnosis = async () => {
    setDiagRunning(true); setDiag(null); setPrinterResult(null);
    const r = await companionPrinterCheck(printerIp.trim());
    setDiagRunning(false);
    if (!r) { await checkPrinter(); return; }
    setDiag(r);
    if (!printerIp.trim() && r.ip) savePrinterIp(r.ip);
  };

  // テストページ: 船内共有端末（locked）はブラウザが無く window.open では何も起きないので、
  // 証明書と同じく Companion 経由で Android の印刷ダイアログに渡す。Companion が無ければブラウザで開く。
  const [testPrinting, setTestPrinting] = useState(false);
  const printTestPage = async () => {
    const url = `${API_BASE_URL}/documents/print-test?token=${encodeURIComponent(getAccessToken() ?? '')}`;
    setTestPrinting(true);
    try {
      const abs = url.startsWith('http') ? url : `${window.location.origin}${url}`;
      const ok = await companionPrint(abs, 'カツオ勤怠 テストページ');
      if (ok) { setPrinterResult('🖨 印刷画面を開きました。プリンターを選んで「印刷」を押してください'); return; }
    } catch { /* フォールバックへ */ }
    finally { setTestPrinting(false); }
    window.open(url, '_blank');
  };

  const handleFaceDelete = async () => {
    setFaceDeleting(true);
    try {
      await apiFetch('/auth/face-data', { method: 'DELETE' });
      removeLocalFace(user.user_id);
      invalidateFaceBadgeCache();
      setFaceRegistered(false);
      setFaceUpdatedAt(null);
      setFaceLocalOnly(false);
      setShowFaceDeleteConfirm(false);
    } catch {
      alert('通信できないため削除できませんでした。電波のある場所でもう一度お試しください。');
    }
    setFaceDeleting(false);
  };

  const refreshSyncState = useCallback(() => {
    setPendingCount(getPendingCount());
    setLastSync(getLastSyncTime());
  }, []);

  useEffect(() => {
    setDeviceVesselName(localStorage.getItem('catms_device_vessel_name'));
    setDeviceToken(localStorage.getItem('catms_device_token'));
    refreshSyncState();
    // 本人の乗船中役職を取得（役職ありなら管理系メニューを表示）
    apiFetch<{ success: boolean; data: { has_position: boolean; position: string | null; user_role: string } }>('/users/me/position')
      .then(r => {
        setHasPosition(!!r?.data?.has_position);
        setMyPosition(r?.data?.position ?? null);
        setFreshRole(r?.data?.user_role ?? null);
      })
      .catch(() => { /* 取得失敗時は非表示のまま（安全側） */ });
  }, [refreshSyncState]);

  const handleSync = async () => {
    if (getPendingCount() === 0) { setSyncResult('送信するデータがありません'); setTimeout(() => setSyncResult(null), 3000); return; }
    setSyncing(true);
    setSyncResult(null);
    // 完了リスナーを一時登録
    const unsubscribe = onSyncComplete(({ synced, failed }) => {
      if (synced > 0 && failed === 0) setSyncResult(`✅ ${synced}件を送信しました`);
      else if (synced > 0) setSyncResult(`⚠️ ${synced}件送信・${failed}件失敗`);
      else if (failed > 0) setSyncResult('❌ 送信に失敗しました（オフラインの可能性）');
      setSyncing(false);
      refreshSyncState();
      setTimeout(() => setSyncResult(null), 4000);
      unsubscribe();
    });
    try {
      await triggerSync();
    } catch {
      setSyncResult('❌ 送信に失敗しました');
      setSyncing(false);
      refreshSyncState();
      setTimeout(() => setSyncResult(null), 4000);
      unsubscribe();
    }
  };

  const fmtLastSync = (iso: string | null) => {
    if (!iso) return 'なし';
    const d = new Date(iso);
    return `${d.getMonth()+1}/${d.getDate()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  };

  const handleGpsSend = () => {
    const token = localStorage.getItem('catms_device_token');
    if (!token) { setGpsResult('❌ デバイストークンが設定されていません'); return; }
    setGpsSending(true);
    setGpsResult(null);
    const send = async (lat?: number, lon?: number) => {
      const timestamp = new Date().toISOString();
      const payload = `${timestamp}:${lat ?? ''}:${lon ?? ''}`;
      const signature = await computeHmac(token, payload);
      const bat = await batteryPayload();
      apiFetch('/devices/heartbeat', {
        method: 'POST',
        body: JSON.stringify({ device_token: token, company_id: COMPANY_ID, latitude: lat, longitude: lon, timestamp, signature, ...bat }),
      }).then(() => {
        if (lat !== undefined && lon !== undefined) {
          setGpsResult(`✅ GPS送信成功 (${lat.toFixed(5)}, ${lon.toFixed(5)})`);
        } else {
          setGpsResult('⚠️ GPS取得できず（位置情報なしで送信しました）');
        }
      }).catch(() => {
        setGpsResult('❌ 送信失敗（オフラインの可能性）');
      }).finally(() => {
        setGpsSending(false);
        setTimeout(() => setGpsResult(null), 6000);
      });
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => send(pos.coords.latitude, pos.coords.longitude),
        () => send(),
        { timeout: 10000, maximumAge: 0, enableHighAccuracy: true }
      );
    } else {
      send();
    }
  };

  const handleDeviceSaved = (_vesselId: string, vesselName: string, token: string) => {
    setDeviceVesselName(vesselName);
    setDeviceToken(token);
    setShowDeviceSetup(false);
  };

  const handleDeviceClear = async () => {
    const token = localStorage.getItem('catms_device_token');
    if (token) {
      const timestamp = new Date().toISOString();
      const signature = await computeHmac(token, `disconnect:${timestamp}`);
      apiFetch('/devices/disconnect', {
        method: 'POST',
        body: JSON.stringify({ device_token: token, company_id: COMPANY_ID, timestamp, signature }),
      }).catch(() => {});
    }
    localStorage.removeItem('catms_device_token');
    localStorage.removeItem('catms_device_vessel_id');
    localStorage.removeItem('catms_device_vessel_name');
    setDeviceVesselName(null);
    setDeviceToken(null);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
      <AppHeader title="設定" />

      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 24 }}>
        <div style={{ padding: '0 16px' }}>

          {/* プロフィール */}
          <SectionLabel>プロフィール</SectionLabel>
          <div style={{ background: 'white', borderRadius: 14, padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <MyAvatarEditor user={user} size={56} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{user.user_name}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{roleDisplay}</div>
              {deviceVesselName && (
                // 本人の乗船先ではなく、この端末が紐付いている船（紛らわしいので明記する）
                <div style={{ fontSize: 12, color: '#0ea5e9', marginTop: 1, fontWeight: 600 }}>📱 この端末の船：{deviceVesselName}</div>
              )}
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>📷 アイコンを押すと写真を変えられます</div>
            </div>
          </div>

          {/* かんたん画面（大きなボタンだけの画面に切り替える） */}
          {hasFeature('crew_simple_mode') && (<>
          <SectionLabel>かんたん画面</SectionLabel>
          <div style={{ background: 'white', borderRadius: 14, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, marginBottom: 12 }}>
              打刻・記録・連絡・休みの4つだけを大きなボタンで表示します。細かい機能は隠れますが、
              「ふつうの画面にもどす」でいつでも戻せます。
            </div>
            <button
              onClick={() => { const next = !simpleOn; setSimpleMode(next); setSimpleOn(next); nav(next ? '/simple' : '/home'); }}
              style={{
                width: '100%', padding: '16px', borderRadius: 14, cursor: 'pointer', fontSize: 16, fontWeight: 800,
                border: simpleOn ? '2px solid #cbd5e1' : 'none',
                background: simpleOn ? '#fff' : 'linear-gradient(145deg,#0057d9,#003fa3)',
                color: simpleOn ? '#475569' : '#fff',
              }}>
              {simpleOn ? 'ふつうの画面にもどす' : 'かんたん画面にする'}
            </button>
          </div>
          </>)}

          {/* 文字の大きさ（この端末だけの設定。小さくて読みにくい場合に大きくする） */}
          {hasFeature('crew_font_size') && (<>
          <SectionLabel>文字の大きさ</SectionLabel>
          <div style={{ background: 'white', borderRadius: 14, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {FONT_SCALE_OPTIONS.map(o => {
                const active = fontScale === o.value;
                return (
                  <button key={o.value}
                    onClick={() => { setFontScale(o.value); setFontScaleState(o.value); }}
                    style={{
                      flex: 1, padding: '14px 0', borderRadius: 12, cursor: 'pointer',
                      border: active ? '2px solid #0057d9' : '1.5px solid #e2e8f0',
                      background: active ? '#eff6ff' : '#fff',
                      color: active ? '#0057d9' : '#475569',
                      fontWeight: 800,
                      fontSize: o.value === 'small' ? 15 : o.value === 'medium' ? 18 : 21,
                    }}>
                    {o.label}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 10, lineHeight: 1.6 }}>
              この端末の表示だけが変わります（他の人の画面や記録には影響しません）。
            </div>
          </div>
          </>)}

          {/* 画面上の余白（ノッチ／カメラ穴で見出しが隠れる端末の調整。端末ごとの設定） */}
          <SectionLabel>画面上の余白</SectionLabel>
          <div style={{ background: 'white', borderRadius: 14, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {HEADER_OFFSET_OPTIONS.map(o => {
                const active = headerOffset === o.value;
                return (
                  <button key={o.value}
                    onClick={() => { setHeaderOffset(o.value); setHeaderOffsetState(o.value); }}
                    style={{
                      flex: 1, padding: '12px 0', borderRadius: 12, cursor: 'pointer',
                      border: active ? '2px solid #0057d9' : '1.5px solid #e2e8f0',
                      background: active ? '#eff6ff' : '#fff',
                      color: active ? '#0057d9' : '#475569',
                      fontWeight: 800, fontSize: 12.5, lineHeight: 1.3,
                    }}>
                    {o.label}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 10, lineHeight: 1.6 }}>
              画面いちばん上の見出しが、スマホのカメラ穴や時計の表示にかかって読めないときは「広い」を選んでください。
              逆に上が空きすぎるときは「せまい」にできます。すべての画面にすぐ反映されます（この端末だけの設定）。
            </div>
          </div>

          {/* デバイス設定（管理者・役職者のみ） */}
          {privileged && (<>
          <SectionLabel>このデバイス</SectionLabel>
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            {deviceVesselName ? (
              <>
                <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 11, background: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🚢</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{deviceVesselName} に紐付け済み</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontFamily: 'monospace' }}>
                      {deviceToken ? deviceToken.slice(0, 18) + '...' : ''}
                    </div>
                  </div>
                  <span style={{ fontSize: 18, color: '#10b981' }}>✅</span>
                </div>
                <Divider />
                <button onClick={() => setShowDeviceSetup(true)} style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ width: 38, height: 38, borderRadius: 11, background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🔄</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>船舶を変更する</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>新しいトークンを入力して変更</div>
                  </div>
                </button>
                <Divider />
                <button onClick={handleDeviceClear} style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ width: 38, height: 38, borderRadius: 11, background: '#fff1f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🗑</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#ef4444' }}>デバイス設定を解除</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>船舶との紐付けを削除します</div>
                  </div>
                </button>
              </>
            ) : (
              <button onClick={() => setShowDeviceSetup(true)} style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 38, height: 38, borderRadius: 11, background: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📱</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>デバイストークンを設定する</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>この端末を特定の船舶に紐付けます</div>
                </div>
                <span style={{ color: '#cbd5e1', fontSize: 18 }}>›</span>
              </button>
            )}
          </div>

          {/* アルコールセンサー状態 */}
          <SectionLabel>アルコールセンサー状態</SectionLabel>
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '16px' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12 }}>
              <span style={{ fontSize: 28, flexShrink: 0 }}>🧪</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>センサー情報</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 3, lineHeight: 1.6 }}>
                  息を吹いた状態で {fmtMgL(alcoTh.critical_mgl)} 未満であれば正常に出勤できます。<br />
                  {fmtMgL(alcoTh.critical_mgl)} 以上の場合は出勤不可となります。
                </div>
              </div>
            </div>
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#0369a1', fontWeight: 600, marginBottom: 4 }}>判定基準</div>
              <div style={{ fontSize: 13, color: '#0c4a6e' }}>
                • 出勤可: <strong>{fmtMgL(alcoTh.critical_mgl)} 未満</strong><br />
                • 要上長確認: <strong>{fmtMgL(alcoTh.warning_mgl)} 以上 {fmtMgL(alcoTh.critical_mgl)} 未満</strong><br />
                • 出勤不可: <strong>{fmtMgL(alcoTh.critical_mgl)} 以上</strong>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6, marginBottom: 8 }}>
              ※ 検知器は低い濃度での誤検知を避けるため、0.050 mg/L 以下は 0.000 mg/L として記録されます（機器メーカーの仕様に合わせています）。
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>
              💡 センサーの調整・校正が必要な場合は管理者にご連絡ください。
            </div>
          </div>

          {/* 通信 */}
          <SectionLabel>通信</SectionLabel>
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            {/* 未送信データ */}
            <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, background: pendingCount > 0 ? '#fffbeb' : '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                {pendingCount > 0 ? '📤' : '✅'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                  未送信データ: <span style={{ color: pendingCount > 0 ? '#d97706' : '#059669' }}>{pendingCount}件</span>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                  最終同期: {fmtLastSync(lastSync)}
                </div>
                {pendingCount > 0 && (
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {getQueue().slice(0, 3).map(item => (
                      <div key={item.id} style={{ fontSize: 11, color: '#64748b', display: 'flex', gap: 6 }}>
                        <span style={{ fontFamily: 'monospace' }}>{item.timestamp_type}</span>
                        <span style={{ color: '#94a3b8' }}>{new Date(item.recorded_at).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    ))}
                    {pendingCount > 3 && <div style={{ fontSize: 11, color: '#94a3b8' }}>...他{pendingCount - 3}件</div>}
                  </div>
                )}
              </div>
            </div>
            <Divider />
            {/* GPS位置情報送信 */}
            <button onClick={handleGpsSend} disabled={gpsSending || !deviceToken}
              style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 14, cursor: (gpsSending || !deviceToken) ? 'not-allowed' : 'pointer', textAlign: 'left', opacity: (gpsSending || !deviceToken) ? 0.7 : 1 }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                {gpsSending ? '⏳' : '📍'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{gpsSending ? 'GPS取得中...' : '位置情報を今すぐ送信'}</div>
                <div style={{ fontSize: 12, color: gpsResult ? (gpsResult.startsWith('✅') ? '#059669' : gpsResult.startsWith('⚠️') ? '#d97706' : '#dc2626') : '#94a3b8', marginTop: 1, fontWeight: gpsResult ? 600 : 400 }}>
                  {gpsResult ?? (deviceToken ? '管理側に現在地を送信します' : 'デバイストークンを先に設定してください')}
                </div>
              </div>
            </button>
            <Divider />
            {/* 今すぐ同期ボタン */}
            <button onClick={handleSync} disabled={syncing}
              style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 14, cursor: syncing ? 'not-allowed' : 'pointer', textAlign: 'left', opacity: syncing ? 0.7 : 1 }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, background: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                {syncing ? '⏳' : '🔄'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{syncing ? '同期中...' : '今すぐ同期'}</div>
                <div style={{ fontSize: 12, color: syncResult ? (syncResult.startsWith('✅') ? '#059669' : '#d97706') : '#94a3b8', marginTop: 1, fontWeight: syncResult ? 600 : 400 }}>
                  {syncResult ?? 'サーバーにデータを送信します'}
                </div>
              </div>
            </button>
          </div>
          </>)}

          {/* アプリの種別切替（管理者・代理ログイン中のみ。動作確認・使い分けの確認用） */}
          {isPrivilegedViewer() && (() => {
            const variant = getAppVariant();
            const cur = variant === 'personal' ? '個人用（閲覧）' : variant === 'managed' ? '管理用（フル操作）' : '自動判定';
            const Btn = (v: 'managed' | 'personal', label: string) => (
              <button onClick={() => switchAppVariant(v)}
                style={{ flex: 1, padding: '11px 8px', borderRadius: 10, border: variant === v ? '2px solid #4338ca' : '1.5px solid #e2e8f0', background: variant === v ? '#eef2ff' : '#fff', color: variant === v ? '#3730a3' : '#475569', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                {label}
              </button>
            );
            return (
              <>
                <SectionLabel>アプリの種別（動作確認用）</SectionLabel>
                <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 18, padding: '14px 16px' }}>
                  <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.6, marginBottom: 10 }}>
                    現在：<strong style={{ color: '#0f172a' }}>{cur}</strong>。<br />
                    <strong>管理用</strong>＝船員の実際の操作・代理の動きを確認（フル操作）。<strong>個人用</strong>＝船員が自分のスマホで見る閲覧専用の画面を確認。
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {Btn('managed', '🖥 管理用')}
                    {Btn('personal', '📱 個人用')}
                  </div>
                </div>
              </>
            );
          })()}

          {/* プリンター（印刷・証明書権限のある人向け。書類/証明書の本体は独立メニュー） */}
          {(() => {
            let perms: Record<string, boolean> = {};
            try { perms = JSON.parse(localStorage.getItem('catms_permissions') ?? '{}'); }
            catch { return null; }
            if (!perms.print_documents && !perms.admin_documents) return null;
            return (
              <>
                <SectionLabel>プリンター</SectionLabel>
                <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 18 }}>
                  {/* プリンター設定支援 */}
                  <div style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
                      <div style={{ width: 38, height: 38, borderRadius: 11, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🖨</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>船内プリンター</div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>接続確認とテスト印刷ができます</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input
                        value={printerIp}
                        onChange={e => savePrinterIp(e.target.value)}
                        placeholder="プリンターのIPアドレス（例: 192.168.1.50）"
                        inputMode="decimal"
                        style={{ flex: 1, padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 13, outline: 'none' }}
                      />
                      <button onClick={runDiagnosis} disabled={diagRunning || printerChecking}
                        style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 12.5, fontWeight: 700, cursor: diagRunning ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
                        {diagRunning || printerChecking ? '診断中…' : '🔎 診断'}
                      </button>
                    </div>
                    {diagRunning && (
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Wi-Fi・プリンター検索・到達・本体状態・印刷サービスの順に調べています（10秒ほど）…</div>
                    )}
                    {diag && (() => {
                      const tone = diag.overall === 'ok' ? { bg: '#ecfdf5', bd: '#a7f3d0', fg: '#047857', label: 'OK' }
                        : diag.overall === 'warn' ? { bg: '#fffbeb', bd: '#fde68a', fg: '#b45309', label: '要確認' }
                        : { bg: '#fef2f2', bd: '#fecaca', fg: '#b91c1c', label: 'NG' };
                      const icon = (st: string) => st === 'ok' ? '✅' : st === 'warn' ? '⚠️' : st === 'ng' ? '❌' : '－';
                      return (
                        <div style={{ border: `1.5px solid ${tone.bd}`, borderRadius: 12, overflow: 'hidden', marginBottom: 10 }}>
                          <div style={{ background: tone.bg, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 13, fontWeight: 900, color: 'white', background: tone.fg, borderRadius: 999, padding: '3px 12px' }}>{tone.label}</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: tone.fg, lineHeight: 1.4 }}>{diag.summary}</span>
                          </div>
                          <div style={{ padding: '4px 12px 8px' }}>
                            {diag.checks.map(c => (
                              <div key={c.key} style={{ display: 'flex', gap: 8, padding: '7px 0', borderBottom: '1px solid #f1f5f9', opacity: c.status === 'skip' ? 0.55 : 1 }}>
                                <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{icon(c.status)}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>{c.label}</div>
                                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, wordBreak: 'break-all' }}>{c.detail}</div>
                                  {c.hint && c.status !== 'ok' && (
                                    <div style={{ fontSize: 12, color: c.status === 'ng' ? '#b91c1c' : '#b45309', lineHeight: 1.5, marginTop: 2 }}>→ {c.hint}</div>
                                  )}
                                </div>
                              </div>
                            ))}
                            {diag.found.length > 1 && (
                              <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 6, lineHeight: 1.6 }}>
                                見つかったプリンター（タップでIPを入れ替え）:{' '}
                                {diag.found.map(f => (
                                  <button key={f.host} onClick={() => savePrinterIp(f.host)}
                                    style={{ margin: '2px 4px 2px 0', padding: '3px 8px', borderRadius: 8, border: '1px solid #cbd5e1', background: 'white', fontSize: 11.5, cursor: 'pointer' }}>
                                    {f.name} {f.host}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    {printerResult && (
                      <div style={{ fontSize: 12, color: printerResult.startsWith('✅') ? '#059669' : '#b45309', background: printerResult.startsWith('✅') ? '#ecfdf5' : '#fffbeb', borderRadius: 8, padding: '8px 10px', marginBottom: 8, lineHeight: 1.6 }}>
                        {printerResult}
                      </div>
                    )}
                    <button onClick={printTestPage} disabled={testPrinting}
                      style={{ width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', background: testPrinting ? '#94a3b8' : 'linear-gradient(145deg,#059669,#0d9488)', color: 'white', fontSize: 14, fontWeight: 800, cursor: testPrinting ? 'wait' : 'pointer' }}>
                      {testPrinting ? '印刷画面を開いています…' : '🖨 テストページを印刷'}
                    </button>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, lineHeight: 1.6 }}>
                      「診断」でWi-Fi・プリンター本体・この端末の印刷機能を順に確認できます。IPが分からないときは空欄のまま診断すると、見つかったプリンターを使います。テストページが開いたら、メニューから「印刷」を選んでプリンターへ出力してください。印刷できれば設定完了です。
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {/* アプリ更新（表示が古いとき用。キャッシュを消して最新を取り直す） */}
          <SectionLabel>アプリ</SectionLabel>
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 18 }}>
            <button onClick={async () => {
              try {
                if ('serviceWorker' in navigator) {
                  const regs = await navigator.serviceWorker.getRegistrations();
                  await Promise.all(regs.map(r => r.unregister().catch(() => {})));
                }
                if (window.caches) { const keys = await caches.keys(); await Promise.all(keys.map(k => caches.delete(k))); }
              } catch { /* ignore */ }
              // クエリを変えてHTTPキャッシュも回避して再読込（?app 等の既存パラメータは保持）
              const u = new URL(window.location.href); u.pathname = '/'; u.searchParams.set('_v', String(Date.now()).slice(-6));
              window.location.replace(u.toString());
            }} style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🔄</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>アプリを最新に更新</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>表示が古い・変更が反映されないときにタップ（キャッシュを消して再読込）</div>
              </div>
              <span style={{ color: '#cbd5e1', fontSize: 18 }}>›</span>
            </button>
          </div>

          {/* アカウント */}
          <SectionLabel>アカウント</SectionLabel>
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            {/* 顔認証: 未登録なら「登録」、登録済みなら状態表示＋「更新」「削除」 */}
            <button onClick={() => nav('/face-register')} style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🙂</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                    {faceRegistered ? '顔認証を更新する' : '顔認証を登録する'}
                  </span>
                  {faceRegistered && (
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: '#059669', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 999, padding: '1px 8px' }}>
                      ✓ 登録済み
                    </span>
                  )}
                  {faceLocalOnly && (
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 999, padding: '1px 8px' }}>
                      この端末のみ
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>
                  {faceRegistered === null
                    ? '登録状態を確認中...'
                    : faceRegistered
                      ? (faceUpdatedAt ? `最終更新: ${faceUpdatedAt.slice(0, 16).replace('T', ' ')} ・ 撮り直して置き換えます` : '撮り直して置き換えます')
                      : '次回から顔認証でログインできます'}
                </div>
              </div>
              <span style={{ color: '#cbd5e1', fontSize: 18 }}>›</span>
            </button>
            {faceRegistered && (
              <>
                <Divider />
                <button onClick={() => setShowFaceDeleteConfirm(true)} style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ width: 38, height: 38, borderRadius: 11, background: '#fff1f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🗑️</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#ef4444' }}>顔認証データを削除</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>サーバとこの端末から顔データを削除します</div>
                  </div>
                </button>
              </>
            )}
            <Divider />
            <button onClick={() => setShowLogoutConfirm(true)} style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, background: '#fff1f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🚪</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#ef4444' }}>ログアウト</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>このデバイスからログアウトします</div>
              </div>
            </button>
          </div>

        </div>
      </div>

      {showFaceDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: '24px 20px 40px', width: '100%', maxWidth: 480 }}>
            <div style={{ fontWeight: 700, fontSize: 17, color: '#0f172a', textAlign: 'center', marginBottom: 8 }}>顔認証データを削除しますか？</div>
            <div style={{ fontSize: 13, color: '#64748b', textAlign: 'center', marginBottom: 16, lineHeight: 1.7 }}>
              サーバとこの端末から顔データが削除され、<br />顔認証でのログイン・打刻ができなくなります。<br />（再登録すればまた使えます）
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowFaceDeleteConfirm(false)} disabled={faceDeleting}
                style={{ flex: 1, padding: 14, borderRadius: 12, border: '1px solid #e2e8f0', background: 'white', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>キャンセル</button>
              <button onClick={handleFaceDelete} disabled={faceDeleting}
                style={{ flex: 1, padding: 14, borderRadius: 12, border: 'none', background: '#ef4444', color: 'white', fontSize: 15, fontWeight: 700, cursor: faceDeleting ? 'wait' : 'pointer', opacity: faceDeleting ? 0.7 : 1 }}>
                {faceDeleting ? '削除中...' : '削除する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogoutConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: '24px 20px 40px', width: '100%', maxWidth: 480 }}>
            <div style={{ fontWeight: 700, fontSize: 17, color: '#0f172a', textAlign: 'center', marginBottom: 8 }}>ログアウトしますか？</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowLogoutConfirm(false)} style={{ flex: 1, padding: 14, borderRadius: 12, border: '1px solid #e2e8f0', background: 'white', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>キャンセル</button>
              <button onClick={onLogout} style={{ flex: 1, padding: 14, borderRadius: 12, border: 'none', background: '#ef4444', color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>ログアウト</button>
            </div>
          </div>
        </div>
      )}

      {showDeviceSetup && (
        <DeviceSetupModal
          onClose={() => setShowDeviceSetup(false)}
          onSaved={handleDeviceSaved}
        />
      )}

      <BottomNav />
    </div>
  );
};
